import React from 'react';
import { Spinner, styled, Text } from '@cx/ui';
import { InitDataDrivenFiltersAction } from 'types';
import AddFilterButton from '../../AddFilterButton';
import DataDrivenFilterPartition from './DataDrivenFiltersPartition';
import { useDataDrivenFilters } from '../hooks';
import {
  DataDrivenFiltersContextType,
  DataDrivenFiltersProvider,
} from '../context';

export type DataDrivenFiltersProps = {
  maxDimensions?: number;
  onFilterAdded?: () => void;
} & InitDataDrivenFiltersAction &
  React.HTMLAttributes<HTMLDivElement> &
  DataDrivenFiltersContextType;

/**
 * component that handles the render logic for the enterprise filters.
 * This component relies on the WorkspaceId to be in the initial state.
 * @param param0
 * @returns
 */
function DataDrivenFilters({
  className = '',
  maxDimensions = 10,
  optionsDebounceTime,
  optionsFilterOption,
  onFilterAdded,
  onInit,
  onPartitionError,
  onPartitionSuccess,
}: DataDrivenFiltersProps) {
  const {
    addDataDrivenFilter,
    dataDrivenFilters,
    partitionOptions,
    selectedPartitions,
    loading,
    originalPartitions,
    onRemoveSource,
    onSourceSelected,
  } = useDataDrivenFilters({
    onInit,
    onPartitionError,
    onPartitionSuccess,
  });

  const handleFilterAdded = () => {
    if (loading) return;
    if (selectedPartitions.length === originalPartitions.length) return;
    addDataDrivenFilter();
    onFilterAdded?.();
  };

  if (loading) {
    return <Spinner />;
  }

  if (originalPartitions.length === 0) {
    return null;
  }

  return (
    <DataDrivenFiltersProvider
      optionsFilterOption={optionsFilterOption}
      optionsDebounceTime={optionsDebounceTime}
    >
      <div className={className}>
        {dataDrivenFilters.map((filter, index) => (
          <div key={filter.data.id}>
            <DataDrivenFilterPartition
              index={index}
              partitionOptions={partitionOptions}
              onRemoveSource={onRemoveSource}
              onSourceSelected={onSourceSelected}
              selectedPartition={selectedPartitions[index] as any}
              maxDimensions={maxDimensions}
            />
          </div>
        ))}
        {selectedPartitions.length === originalPartitions.length ? null : (
          <AddFilterButton
            className="add-partition-button"
            label="Add Partition"
            onClick={handleFilterAdded}
            selectedFilters={dataDrivenFilters}
          />
        )}
        {
          // We limit the number of advanced filters to 10
          selectedPartitions.length === originalPartitions.length ? (
            <Text
              data-testid="cx-data-driven-filters-feedback-message"
              className="cx-data-driven-filters-feedback-message cx2-lang"
            >
              There is only a maximum of {originalPartitions.length} partitions
              to select.
            </Text>
          ) : null
        }
      </div>
    </DataDrivenFiltersProvider>
  );
}

export default styled(DataDrivenFilters)((props) => ({
  '.add-partition-button': {
    paddingTop: props.theme.spacing(1),
  },
  '.cx-data-driven-filters-feedback-message': {
    ...props.theme.typography.smallMedium,
    color: props.theme.palette.warning.main,
    marginTop: props.theme.spacing(0.5),
  },
}));
